import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Check, EyeIcon, EyeOffIcon, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useUpdateCurrentUserPassword } from '~entities/users/api/update-current-user-password';
import { setCurrentUser } from '~entities/users/model/user-store';
import type { UserWithRole } from '~entities/users/model/types';

import { queryClient } from '~app/query-client';
import { QUERY_KEYS } from '~shared/api/query-keys';
import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';

interface UpdatePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const validatePassword = (password: string) => {
  const requirements = {
    uppercase: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    minLength: password.length >= 8,
  };
  return requirements;
};

const updatePasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .refine(
        (password) => /[A-Z]/.test(password),
        'Password must contain at least one uppercase letter',
      )
      .refine(
        (password) => /\d/.test(password),
        'Password must contain at least one number',
      )
      .refine(
        (password) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
        'Password must contain at least one special character',
      ),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type UpdatePasswordFormValues = z.infer<typeof updatePasswordSchema>;

export const SetPasswordDialog = ({
  open,
  onOpenChange,
}: UpdatePasswordDialogProps) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const updatePasswordMutation = useUpdateCurrentUserPassword();

  const updatePasswordForm = useForm<UpdatePasswordFormValues>({
    resolver: zodResolver(updatePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const watchNewPassword = updatePasswordForm.watch('newPassword');
  const [requirements, setRequirements] = useState(
    validatePassword(watchNewPassword),
  );

  useEffect(() => {
    setRequirements(validatePassword(watchNewPassword));
  }, [watchNewPassword]);

  const markPasswordSetupComplete = () => {
    // ENR-4048: clear noPassword before navigate so `_auth` does not bounce
    // back to `/set-password` on a stale CURRENT_USER cache.
    queryClient.setQueryData<UserWithRole>(
      QUERY_KEYS.CURRENT_USER(),
      (current) => {
        if (!current) {
          return current;
        }
        const next = { ...current, noPassword: false };
        setCurrentUser(next);
        return next;
      },
    );
  };

  const onSubmit = async (values: UpdatePasswordFormValues) => {
    try {
      await updatePasswordMutation.mutateAsync({
        currentPassword: '',
        newPassword: values.newPassword,
      });
      markPasswordSetupComplete();
      toast.success('Password set successfully');
      void navigate({ to: '/dashboard' });
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to set password');
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Password</DialogTitle>
          <DialogDescription>
            Choose a new password for your account.
          </DialogDescription>
        </DialogHeader>
        <Form {...updatePasswordForm}>
          <form
            onSubmit={updatePasswordForm.handleSubmit(onSubmit)}
            className='space-y-4'
          >
            <FormField
              control={updatePasswordForm.control}
              name='newPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        {...field}
                        type={showPassword.newPassword ? 'text' : 'password'}
                        placeholder='Enter new password'
                        className='h-11 rounded-md border border-[#d0d7de] bg-white px-4 text-sm text-[#24292f] transition-colors placeholder:text-[#8b949e] hover:border-[#afb8c1] focus:border-[#FFC300] focus:outline-none focus:ring-2 focus:ring-[#FFC300]/40 focus-visible:ring-[#FFC300]/40'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='absolute right-1 top-1/2 h-fit -translate-y-1/2 p-0 hover:bg-transparent'
                        onClick={() => {
                          setShowPassword((prev) => ({
                            ...prev,
                            newPassword: !prev.newPassword,
                          }));
                        }}
                      >
                        {showPassword.newPassword ? (
                          <EyeOffIcon className='size-4' />
                        ) : (
                          <EyeIcon className='size-4' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className='mt-2 space-y-1 text-sm'>
              <div className='grid grid-cols-2 gap-2'>
                <div
                  className={cn(
                    'flex items-center gap-2 text-sm transition-colors duration-200',
                    requirements.minLength
                      ? 'text-green-500'
                      : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full transition-all duration-200',
                      requirements.minLength
                        ? 'bg-green-500 text-white'
                        : 'bg-muted',
                    )}
                  >
                    {requirements.minLength && <Check className='size-3' />}
                  </div>
                  <span>Minimum 8 characters</span>
                </div>
                <div
                  className={cn(
                    'flex items-center gap-2 text-sm transition-colors duration-200',
                    requirements.uppercase
                      ? 'text-green-500'
                      : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full transition-all duration-200',
                      requirements.uppercase
                        ? 'bg-green-500 text-white'
                        : 'bg-muted',
                    )}
                  >
                    {requirements.uppercase && <Check className='size-3' />}
                  </div>
                  <span>At least one uppercase letter</span>
                </div>
              </div>
              <div className='grid grid-cols-2 gap-2'>
                <div
                  className={cn(
                    'flex items-center gap-2 text-sm transition-colors duration-200',
                    requirements.digit
                      ? 'text-green-500'
                      : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full transition-all duration-200',
                      requirements.digit
                        ? 'bg-green-500 text-white'
                        : 'bg-muted',
                    )}
                  >
                    {requirements.digit && <Check className='size-3' />}
                  </div>
                  <span>At least one number</span>
                </div>
                <div
                  className={cn(
                    'flex items-center gap-2 text-sm transition-colors duration-200',
                    requirements.symbol
                      ? 'text-green-500'
                      : 'text-muted-foreground',
                  )}
                >
                  <div
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-full transition-all duration-200',
                      requirements.symbol
                        ? 'bg-green-500 text-white'
                        : 'bg-muted',
                    )}
                  >
                    {requirements.symbol && <Check className='size-3' />}
                  </div>
                  <span>{`At least one special character (!@#$%^&*(),.?":{|})<>`}</span>
                </div>
              </div>
            </div>
            <FormField
              control={updatePasswordForm.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Password</FormLabel>
                  <FormControl>
                    <div className='relative'>
                      <Input
                        {...field}
                        type={
                          showPassword.confirmPassword ? 'text' : 'password'
                        }
                        placeholder='Confirm new password'
                        className='h-11 rounded-md border border-[#d0d7de] bg-white px-4 text-sm text-[#24292f] transition-colors placeholder:text-[#8b949e] hover:border-[#afb8c1] focus:border-[#FFC300] focus:outline-none focus:ring-2 focus:ring-[#FFC300]/40 focus-visible:ring-[#FFC300]/40'
                      />
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='absolute right-1 top-1/2 h-fit -translate-y-1/2 p-0 hover:bg-transparent'
                        onClick={() => {
                          setShowPassword((prev) => ({
                            ...prev,
                            confirmPassword: !prev.confirmPassword,
                          }));
                        }}
                      >
                        {showPassword.confirmPassword ? (
                          <EyeOffIcon className='size-4' />
                        ) : (
                          <EyeIcon className='size-4' />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  type='button'
                  variant='outline'
                  disabled={updatePasswordMutation.isPending}
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button type='submit' disabled={updatePasswordMutation.isPending}>
                {updatePasswordMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Setting Password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
